import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Loader from 'ui/atoms/Loader';

const propTypes = {
  /** When true, shows a single full-area blocking layer (opaque) so underlying routes are not visible. */
  isLoading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * ExchangeLoadingShell component.
 * Wraps exchange route content: children (Switch) stay mounted; one overlay Loader when loading.
 * Keeping routes mounted avoids remounting the active route — and re-running its fetches — every
 * time an exchange operation toggles the loading flag.
 * @param {object} props - Component props.
 * @param {boolean} props.isLoading - Whether exchange work is in flight.
 * @param {React.ReactNode} props.children - Route content to keep mounted beneath the overlay.
 * @returns {JSX.Element}
 */
const ExchangeLoadingShell = memo(function ExchangeLoadingShell({ isLoading, children }) {
  return (
    <div className="relative isolate w-full min-h-[min(60vh,32rem)]">
      {/* `inert` takes the covered routes out of the tab order as well as the pointer's reach, which
          `aria-hidden` alone does not. React 16 passes it through as a plain attribute, so it takes
          an empty string rather than `true`. The other two stay for browsers without `inert`. */}
      <div
        className={isLoading ? 'pointer-events-none select-none' : undefined}
        aria-hidden={isLoading ? true : undefined}
        inert={isLoading ? '' : undefined}
      >
        {children}
      </div>
      {isLoading && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-[#f3f3f3]"
          role="status"
          aria-busy="true"
        >
          <Loader />
        </div>
      )}
    </div>
  );
});

ExchangeLoadingShell.propTypes = propTypes;

export default ExchangeLoadingShell;
